import React, { useEffect, useRef } from 'react';
import {
    View,
    Text,
    Image,
    TouchableOpacity,
    ScrollView,
    StyleSheet,
    Platform,
    Dimensions,
    Animated,
} from 'react-native';

import { useNavigation } from '../context/NavigationContext';
import { useTheme } from '../context/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');

const LandingScreen = () => {
    const { palette, mode } = useTheme();
    const navigation = useNavigation();
    
    // Animation values
    const cardScale = useRef(new Animated.Value(0.85)).current;
    const cardOpacity = useRef(new Animated.Value(0)).current;
    const leftCharX = useRef(new Animated.Value(-300)).current;
    const leftCharOpacity = useRef(new Animated.Value(0)).current;
    const leftCharRotate = useRef(new Animated.Value(-45)).current;
    const rightCharX = useRef(new Animated.Value(300)).current;
    const rightCharOpacity = useRef(new Animated.Value(0)).current;
    const bookButtonY = useRef(new Animated.Value(-50)).current;
    const bookButtonOpacity = useRef(new Animated.Value(0)).current;
    const headingOpacity = useRef(new Animated.Value(0)).current;
    const headingY = useRef(new Animated.Value(30)).current;
    const subheadingOpacity = useRef(new Animated.Value(0)).current;
    const buttonOpacity = useRef(new Animated.Value(0)).current;
    const buttonScale = useRef(new Animated.Value(0.8)).current;
    
    useEffect(() => {
        // Main card entrance
        Animated.parallel([
            Animated.spring(cardScale, {
                toValue: 1,
                tension: 45,
                friction: 7,
                useNativeDriver: true,
            }),
            Animated.timing(cardOpacity, {
                toValue: 1,
                duration: 450,
                useNativeDriver: true,
            }),
        ]).start();

        // Left character - swing in
        Animated.sequence([
            Animated.delay(200),
            Animated.parallel([
                Animated.spring(leftCharX, {
                    toValue: 0,
                    tension: 35,
                    friction: 8,
                    useNativeDriver: true,
                }),
                Animated.timing(leftCharOpacity, {
                    toValue: 1,
                    duration: 600,
                    useNativeDriver: true,
                }),
                Animated.spring(leftCharRotate, {
                    toValue: 0,
                    tension: 30,
                    friction: 6,
                    useNativeDriver: true,
                }),
            ]),
        ]).start();

        // Right character - slide in
        Animated.sequence([
            Animated.delay(350),
            Animated.parallel([
                Animated.spring(rightCharX, {
                    toValue: 0,
                    tension: 35,
                    friction: 8,
                    useNativeDriver: true,
                }),
                Animated.timing(rightCharOpacity, {
                    toValue: 1,
                    duration: 600,
                    useNativeDriver: true,
                }),
            ]),
        ]).start();

        // Book button - drop in
        Animated.sequence([
            Animated.delay(500),
            Animated.parallel([
                Animated.spring(bookButtonY, {
                    toValue: 0,
                    tension: 50,
                    friction: 7,
                    useNativeDriver: true,
                }),
                Animated.timing(bookButtonOpacity, {
                    toValue: 1,
                    duration: 400,
                    useNativeDriver: true,
                }),
            ]),
        ]).start();

        // Staggered text animations
        Animated.sequence([
            Animated.delay(300),
            Animated.parallel([
                Animated.timing(headingOpacity, { toValue: 1, duration: 500, useNativeDriver: true }),
                Animated.spring(headingY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
            ]),
        ]).start();

        Animated.sequence([
            Animated.delay(450),
            Animated.timing(subheadingOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        ]).start();

        // Button - pop in
        Animated.sequence([
            Animated.delay(600),
            Animated.parallel([
                Animated.timing(buttonOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
                Animated.spring(buttonScale, { toValue: 1, tension: 60, friction: 6, useNativeDriver: true }),
            ]),
        ]).start();
    }, []);

    const leftCharRotateInterpolate = leftCharRotate.interpolate({
        inputRange: [-45, 0],
        outputRange: ['-45deg', '-30deg'],
    });

    return (
        <View id="landing-screen" style={styles.container}>
            <View style={styles.contentGroup}>
                {/* Left Character */}
                <Animated.View 
                    style={[
                        styles.leftCharacter,
                        {
                            opacity: leftCharOpacity,
                            transform: [
                                { translateX: leftCharX },
                                { rotate: leftCharRotateInterpolate },
                            ],
                        }
                    ]}
                >
                    <Image
                        source={require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_HighRisk_v01_SL.png')}
                        style={styles.characterImageLeft}
                        resizeMode="contain"
                    />
                </Animated.View>

                {/* Right Character */}
                <Animated.View 
                    style={[
                        styles.rightCharacter,
                        {
                            opacity: rightCharOpacity,
                            transform: [{ translateX: rightCharX }],
                        }
                    ]}
                >
                    <Image
                        source={require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_PointLeft_v01_SL.png')}
                        style={styles.characterImageRight}
                        resizeMode="contain"
                    />
                </Animated.View>

                {/* Book Button - Overlapping main card */}
                <Animated.View
                    style={[
                        styles.bookButtonWrapper,
                        {
                            opacity: bookButtonOpacity,
                            transform: [{ translateY: bookButtonY }],
                        }
                    ]}
                >
                <TouchableOpacity
                    style={styles.bookButton}
                    onPress={() => {/* Handle book navigation */}}
                >
                    <Text style={styles.bookButtonText}>Get the Book Here!</Text>
                </TouchableOpacity>
                </Animated.View>

                {/* Main Content Card */}
                <Animated.View 
                    style={[
                        styles.mainCard,
                        {
                            opacity: cardOpacity,
                            transform: [{ scale: cardScale }],
                        }
                    ]}
                >
                    <Animated.Text 
                        style={[
                            styles.headingText,
                            {
                                opacity: headingOpacity,
                                transform: [{ translateY: headingY }],
                            }
                        ]}
                    >
                    Everything you need to know to take your business{' '}
                    <Text style={styles.highlightText}>
                        to seven figures in twelve months
                    </Text>
                    </Animated.Text>

                    <Animated.Text style={[styles.subheading, { opacity: subheadingOpacity }]}>
                    Stay on track with our patented method
                    </Animated.Text>

                {/* Get Started Button */}
                    <Animated.View
                        style={{
                            opacity: buttonOpacity,
                            transform: [{ scale: buttonScale }],
                        }}
                    >
                <TouchableOpacity
                    style={[styles.button, styles.getStartedButton]}
                    onPress={() => navigation.navigateTo('Login')}
                >
                    <Text style={styles.getStartedButtonText}>Get Started</Text>
                </TouchableOpacity>
                    </Animated.View>
                </Animated.View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        minHeight: '100vh',
        paddingTop: 50,
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'center',
        overflow: 'hidden',
    },
    contentGroup: {
        position: 'relative',
        maxWidth: 1000,
        width: '100%',
        alignItems: 'center',
    },

    leftCharacter: {
        position: 'absolute',
        left: "-32%",
        top: '50%',
        marginTop: -275, // translateY(-50%) equivalent for 550px height
        zIndex: 1,
    },
    bookButtonWrapper: {
        position: 'absolute',
        bottom: -40,
        left: -60,
        zIndex: 10,
    },
    rightCharacter: {
        position: 'absolute',
        right: '-30%',
        top: '125%',
        marginTop: -350, // translateY(-50%) equivalent
        zIndex: 5,
    },
    characterImageLeft: {
        width: 550,
        height: 550,
    },
    characterImageRight: {
        width: 700,
        height: 700,
    },
    mainCard: {
        backgroundColor: '#FF4747',
        borderRadius: 24,
        padding: 32,
        maxWidth: 1000,
        alignItems: 'center',
        zIndex: 3,
        gap: 24,
        ...Platform.select({
            web: {
                boxShadow: '5px 7px 0px rgba(0,0,0,1)',
            },
            default: {
                shadowColor: '#000000',
                shadowOffset: { width: 5, height: 7 },
                shadowOpacity: 1,
                shadowRadius: 0,
                elevation: 8,
            },
        }),
    },
    headingText: {
        textAlign: 'center',
        fontSize: 64,
        fontFamily: 'Chivo',
        fontWeight: '600',
        lineHeight: 76,
        color: '#F1F1F1',
        ...Platform.select({
            web: {
                textShadow: '2px 5px 1px rgba(0,0,0,1)',
            },
            default: {
                textShadowColor: 'rgba(0,0,0,1)',
                textShadowOffset: { width: 2, height: 2 },
                textShadowRadius: 1,
            },
        }),
    },
    highlightText: {
        color: '#F7DF7B',
        fontWeight: '600',
    },
    subheading: {
        textAlign: 'center',
        color: '#F1F1F1',
        fontSize: 28,
        fontFamily: 'Plus Jakarta Sans',
        fontWeight: '400',
        lineHeight: 32,
         ...Platform.select({
            web: {
            textShadow: '2px 2px 1px rgba(0,0,0,1)',
            },
            default: {
            textShadowColor: 'rgba(0,0,0,1)',
            textShadowOffset: { width: 2, height: 2 },
            textShadowRadius: 1,
            }
        })
    },
    button: {
        paddingVertical: 16,
        paddingHorizontal: 32,
        borderRadius: 12,
        minWidth: 180,
        alignItems: 'center',
        justifyContent: 'center',
    },
    getStartedButton: {
        backgroundColor: '#18B3B7',
        width: 250,
        ...Platform.select({
            web: {
                boxShadow: '5px 7px 0px rgba(0,0,0,1)',
            },
            default: {
                shadowColor: '#000000',
                shadowOffset: { width: 5, height: 7 },
                shadowOpacity: 1,
                shadowRadius: 0,
                elevation: 8,
            },
        }),
    },
    getStartedButtonText: {
        color: 'white',
        fontSize: 18,
        fontFamily: 'Chivo',
        fontWeight: '600',
        ...Platform.select({
            web: {
            textShadow: '2px 2px 1px rgba(0,0,0,1)',
            },
            default: {
            textShadowColor: 'rgba(0,0,0,1)',
            textShadowOffset: { width: 2, height: 2 },
            textShadowRadius: 1,
            }
        })
    },
    bookButton: {
        maxWidth: 300,
        backgroundColor: '#9D9BCC',
        paddingVertical: 30,
        paddingHorizontal: 30,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        ...Platform.select({
            web: {
                boxShadow: '5px 7px 0px rgba(0,0,0,1)',
            },
            default: {
                shadowColor: '#000000',
                shadowOffset: { width: 5, height: 7 },
                shadowOpacity: 1,
                shadowRadius: 0,
                elevation: 8,
            },
        }),
    },
    bookButtonText: {
        color: 'white',
        fontSize: 42,
        fontFamily: 'Chivo',
        fontWeight: '600',
        textAlign: 'center',
        ...Platform.select({
            web: {
            textShadow: '2px 2px 1px rgba(0,0,0,1)',
            },
            default: {
            textShadowColor: 'rgba(0,0,0,1)',
            textShadowOffset: { width: 2, height: 2 },
            textShadowRadius: 1,
            }
        })
    },
});

export default LandingScreen;

